import { Box, Divider, IconButton, Stack } from '@mui/material'
import { ReactComponent as ArrowCloseDone } from 'assets/arrow_close_done.svg'
import { usePageContainerWidth } from 'components/farm-dashboard/components/DashboardGrid/hooks'
import { calculateOptimalCellSize } from 'components/farm-dashboard/constants/constants'
import { WidgetPreviewProvider } from 'components/farm-dashboard/context/WidgetPreviewContext'
import React, { useCallback, useEffect, useRef } from 'react'
import { closeWidgetDock, openWidgetDock } from 'store/dashboardSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { Scrollbar } from 'ui/scrollbar/Scrollbar'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import SidebarWidgets from '../sidebar-widgets/SidebarWidgets'

type WidgetContainerProps = {
	children?: React.ReactNode
}

export const DockContainer: React.FC<WidgetContainerProps> = ({ children }) => {
	const dispatch = useAppDispatch()
	const { isWidgetDockExpanded, isWidgetDockVisible } = useAppSelector(
		state => state.dashboardSlice,
	)
	const prevVisibleRef = useRef(false)
	const contentRef = useRef<HTMLDivElement>(null)
	const contentWidth = usePageContainerWidth(contentRef)
	const cellSize =
		contentWidth > 0 ? calculateOptimalCellSize(contentWidth) : 168

	const onHeaderClick = useCallback(() => {
		if (isWidgetDockExpanded) {
			dispatch(closeWidgetDock())
		} else {
			dispatch(openWidgetDock())
		}
	}, [dispatch, isWidgetDockExpanded])

	useEffect(() => {
		const wasVisible = prevVisibleRef.current
		if (!wasVisible && isWidgetDockVisible) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					dispatch(openWidgetDock())
				})
			})
		}
		prevVisibleRef.current = isWidgetDockVisible
	}, [dispatch, isWidgetDockVisible])

	return (
		<Box
			sx={{
				position: 'fixed',
				bottom: 0,
				transform: isWidgetDockExpanded ? 'translateY(0)' : 'translateY(390px)',
				right: 20,
				boxShadow: COLORS.shadowSecondary,
				borderTopLeftRadius: 12,
				borderTopRightRadius: 12,
				overflow: 'hidden',
				width: 'calc(100vw - 266px)',
				bgcolor: 'white',
				transition: 'transform 300ms ease-in-out',
				willChange: 'transform',
			}}
		>
			<Stack
				direction='row'
				alignItems='center'
				justifyContent='flex-end'
				paddingRight='24px'
				sx={{
					height: 30,
					cursor: 'pointer',
				}}
				onClick={onHeaderClick}
			>
				<Stack
					direction='row'
					gap='8px'
					alignItems='center'
					sx={{ justifyContent: 'flex-end' }}
				>
					<TypographySecondary sx={{ color: COLORS.secondary }}>
						{isWidgetDockExpanded ? 'Скрыть' : 'Раскрыть'}
					</TypographySecondary>
					<IconButton
						onClick={onHeaderClick}
						sx={{
							p: 0,
							transform: isWidgetDockExpanded ? 'rotate(180deg)' : 'none',
							transition: 'transform 200ms ease',
						}}
					>
						<ArrowCloseDone />
					</IconButton>
				</Stack>
			</Stack>
			<Stack
				direction='row'
				p='0 24px'
				sx={{
					width: '100%',
					height: 430,
				}}
			>
				<SidebarWidgets />
				<Divider orientation='vertical' flexItem />
				<Scrollbar sx={{ width: '100%', padding: '8px 24px 80px 24px' }}>
					<div ref={contentRef} style={{ width: '100%' }}>
						<WidgetPreviewProvider isPreview={true} cellSize={cellSize}>
							{children}
						</WidgetPreviewProvider>
					</div>
				</Scrollbar>
			</Stack>
		</Box>
	)
}
